/* Rest between sets: how long (per plan exercise), the running timer and
 * when it should tick or beep. The timer is device-local: it lives in root
 * state plus localStorage, never in the synced blob (docs/fitness-data-model.md),
 * so +15 or Skip costs no server write and a pull never resets it. Pure,
 * except read/write of the two localStorage keys. */

export const REST_KEY = 'xceller_fit_rest';
export const KEEP_AWAKE_KEY = 'xceller_fit_keep_awake';
export const REST_CHOICES = [60, 90, 120, 150, 180, 240];
export const WARN_SECONDS = 10; // soft tick before the end
export const GO_SECONDS = 10; // accent "GO" pill after the end
export const STALE_SECONDS = 600; // an ended timer goes away after 10 min
// An alert only fires this close to its moment; later (back from the
// background, a reload) it would be stale noise.
const ALERT_WINDOW_MS = 2000;

const isPosNum = v => typeof v === 'number' && Number.isFinite(v) && v > 0;

/* Lowercase words separated by single spaces, padded so ' row ' matches
 * whole words only ('Barbell Row (overhand)' -> ' barbell row overhand '). */
function words(name) {
  return ` ${String(name || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()} `;
}
const HEAVY = [' deadlift', ' squat', ' bench press', ' overhead press', ' barbell row', ' pendlay row',
  ' t bar row', ' leg press', ' hip thrust'];
const COMPOUND = /\b(press|rows?|pulldowns?|pull ?ups?|chin ?ups?|dips?|lunges?|split squat|step ?ups?|rdl|romanian|good morning|hack|thrust|push ?ups?)\b/;
// Single-joint work whose name happens to contain a compound word.
const NOT_COMPOUND = /\b(straight arm|upright|svend|calf|calves)\b/;

export function isHeavyCompound(name) {
  const w = words(name);
  return HEAVY.some(k => w.includes(`${k} `) || w.includes(`${k}s `));
}

/**
 * Rest in seconds after a set of this plan exercise: its restSec, else
 * heavy compounds at <= 8 reps 180, other compounds 150, isolation 90.
 * @param {{name: string, restSec?: number, reps?: number, repMax?: number}} ex
 *   a plan exercise; an ad-hoc exercise passes { name, reps }
 */
export function restSecondsFor(ex) {
  if (isPosNum(ex?.restSec)) return Math.round(ex.restSec);
  const name = ex?.name;
  const reps = isPosNum(ex?.repMax) ? ex.repMax : (isPosNum(Number(ex?.reps)) ? Number(ex.reps) : null);
  const w = words(name);
  if (NOT_COMPOUND.test(w)) return 90;
  if (isHeavyCompound(name)) return reps === null || reps <= 8 ? 180 : 150;
  return COMPOUND.test(w) ? 150 : 90;
}

/* m:ss, never negative. */
export function formatClock(sec) {
  const s = Math.max(0, Math.round(sec || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

function cleanNextSet(n) {
  if (!n || typeof n !== 'object' || typeof n.exercise !== 'string') return null;
  const num = v => (typeof v === 'number' && Number.isFinite(v) ? v : null);
  return { exercise: n.exercise, n: num(n.n), of: num(n.of), weight: num(n.weight), reps: num(n.reps) };
}

/**
 * A new timer. `total` is the whole rest in seconds and `endsAt - total`
 * stays its start, so the progress bar survives -15/+15 and the chips.
 * @param {{sec: number, exercise?: string, planExId?: string, nextSet?: object}} info
 *   exercise = the one just done; planExId = its plan slot ("Remember" writes
 *   restSec there); nextSet = {exercise, n, of, weight, reps} for the GO line
 * @param {number} now epoch ms
 */
export function makeRestTimer(info, now) {
  const total = Math.max(1, Math.round(info?.sec || 0));
  return {
    endsAt: now + total * 1000, total,
    exercise: typeof info?.exercise === 'string' ? info.exercise : null,
    planExId: typeof info?.planExId === 'string' ? info.planExId : null,
    nextSet: cleanNextSet(info?.nextSet),
  };
}

/* -15 / +15. Never ends before now; +15 on a finished rest counts from
 * now, and -15 leaves a finished rest alone (it would beep again). */
export function adjustRestTimer(timer, deltaSec, now) {
  if (deltaSec < 0 && timer.endsAt <= now) return timer;
  const start = timer.endsAt - timer.total * 1000;
  const from = deltaSec > 0 ? Math.max(timer.endsAt, now) : timer.endsAt;
  const endsAt = Math.max(now, from + deltaSec * 1000);
  return { ...timer, endsAt, total: Math.max(1, Math.round((endsAt - start) / 1000)) };
}

/* A chip: the rest becomes `sec` long from when it started. A length that
 * has already passed ends a running rest now and leaves a finished one be. */
export function setRestLength(timer, sec, now) {
  const start = timer.endsAt - timer.total * 1000;
  const endsAt = start + Math.max(1, Math.round(sec)) * 1000;
  if (endsAt > now) return { ...timer, endsAt, total: Math.round((endsAt - start) / 1000) };
  const end = Math.min(timer.endsAt, now);
  return { ...timer, endsAt: end, total: Math.max(1, Math.round((end - start) / 1000)) };
}

/**
 * Where a timer is at `now`.
 * @returns {{phase: 'idle'|'running'|'go'|'over'|'stale', remaining?: number,
 *   progress?: number, overBy?: number}} remaining/overBy in whole seconds;
 *   progress 0..1 of the rest done
 */
export function restPhase(timer, now) {
  if (!timer || !isPosNum(timer.endsAt)) return { phase: 'idle' };
  const ms = timer.endsAt - now;
  if (ms > 0) {
    const total = isPosNum(timer.total) ? timer.total * 1000 : ms;
    return { phase: 'running', remaining: Math.ceil(ms / 1000), progress: Math.min(1, Math.max(0, 1 - ms / total)) };
  }
  const overBy = Math.floor((now - timer.endsAt) / 1000);
  if (overBy >= STALE_SECONDS) return { phase: 'stale', overBy };
  return { phase: overBy < GO_SECONDS ? 'go' : 'over', overBy };
}

/**
 * Which alerts are due now. `fired` remembers the endsAt each alert last
 * fired for, so +15 (a new endsAt) arms them again and a re-render never
 * repeats one. Alerts only fire within 2 s of their moment.
 * @param {{warnFor: number|null, goFor: number|null}} fired
 * @returns {{tick: boolean, go: boolean, fired: {warnFor, goFor}}}
 */
export function dueAlerts(timer, now, fired) {
  const f = fired || { warnFor: null, goFor: null };
  if (!timer || !isPosNum(timer.endsAt)) return { tick: false, go: false, fired: f };
  const ms = timer.endsAt - now;
  const tick = ms > 0 && ms <= WARN_SECONDS * 1000 && ms > WARN_SECONDS * 1000 - ALERT_WINDOW_MS
    && f.warnFor !== timer.endsAt;
  const go = ms <= 0 && -ms < ALERT_WINDOW_MS && f.goFor !== timer.endsAt;
  return {
    tick, go,
    fired: { warnFor: tick ? timer.endsAt : f.warnFor, goFor: go ? timer.endsAt : f.goFor },
  };
}

/* 'Hammer Curl 3/4 · 16 kg × 10' (parts that are unknown are left out). */
export function nextSetText(nextSet) {
  if (!nextSet) return '';
  const { exercise, n, of, weight, reps } = nextSet;
  const nums = weight !== null && reps !== null ? `${weight} kg × ${reps}`
    : reps !== null ? `${reps} reps`
    : weight !== null ? `${weight} kg` : '';
  return [`${exercise}${n ? ` ${n}${of ? `/${of}` : ''}` : ''}`, nums].filter(Boolean).join(' · ');
}

/* The running pill's second line: 'Set 3/4 · 16 kg × 10' while the next set
 * is of the exercise just done, else the full nextSetText. */
export function nextSetLine(timer) {
  const n = timer?.nextSet;
  if (!n) return '';
  if (n.exercise === timer.exercise && n.n) return nextSetText({ ...n, exercise: 'Set' });
  return nextSetText(n);
}

/* The saved timer, or null when missing, malformed or long over. */
export function readRestTimer(now = Date.now()) {
  try {
    const raw = JSON.parse(localStorage.getItem(REST_KEY) || 'null');
    if (!raw || !isPosNum(raw.endsAt) || !isPosNum(raw.total)) return null;
    if (now - raw.endsAt >= STALE_SECONDS * 1000) return null;
    return {
      endsAt: raw.endsAt, total: raw.total,
      exercise: typeof raw.exercise === 'string' ? raw.exercise : null,
      planExId: typeof raw.planExId === 'string' ? raw.planExId : null,
      nextSet: cleanNextSet(raw.nextSet),
    };
  } catch { return null; }
}

export function writeRestTimer(timer) {
  try {
    if (timer) localStorage.setItem(REST_KEY, JSON.stringify(timer));
    else localStorage.removeItem(REST_KEY);
  } catch {}
}

export function readKeepAwake() {
  try { return localStorage.getItem(KEEP_AWAKE_KEY) === '1'; } catch { return false; }
}

export function writeKeepAwake(on) {
  try { localStorage.setItem(KEEP_AWAKE_KEY, on ? '1' : '0'); } catch {}
}

/**
 * Plan exercise `exId` with restSec = sec; null or 0 removes restSec (the
 * key is deleted, not set to undefined, which the sync hash would read as
 * null). Days without the exercise keep their identity.
 */
export function withPlanRest(state, exId, sec) {
  const plan = (state.plan || []).map(day => {
    if (!day.exercises?.some(e => e.id === exId)) return day;
    return {
      ...day,
      exercises: day.exercises.map(e => {
        if (e.id !== exId) return e;
        const { restSec: _old, ...rest } = e;
        return isPosNum(sec) ? { ...rest, restSec: Math.round(sec) } : rest;
      }),
    };
  });
  return { ...state, plan };
}
